import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from '../Header';
import './tracker.css';
import TrackerIcon from './TrackerIcon';
import { catalystTrackerApi } from '../../services/catalystTrackerApi';
import { TrackerCatalyst, TrackerChangelogEntry, TrackerConference, TrackerRegistryDoc, TrackerSuggestion, TrackerSyncMeta, TrackerWatchDoc } from '../../types';
import { useTrackerPermissions } from './useTrackerPermissions';
import CatalystStatsBar from './CatalystStatsBar';
import CatalystFilterBar from './CatalystFilterBar';
import CatalystListView from './CatalystListView';
import CatalystCalendarView from './CatalystCalendarView';
import ChangelogView from './ChangelogView';
import WatchPanel from './WatchPanel';
import PerspectivePanel from './PerspectivePanel';
import SuggestCatalystModal from './SuggestCatalystModal';
import SuggestionsQueueModal from './SuggestionsQueueModal';
import EmailAlertsModal from './EmailAlertsModal';
import TrackerModal from './TrackerModal';
import TimelineBanner from './TimelineBanner';
import { DEFAULT_FILTERS, TrackerFilterState, TrackerSort, applyView, baseFilter, fmtLong, snapshotView, sortCatalysts, visibleCatalysts } from './helpers';
import { perspOf } from './readThrough';

const REFRESH_MS = 30000;
const emptyWatch = (): TrackerWatchDoc => ({ catalysts: [], views: [], email: '', emailAlerts: false, read: {}, searchLog: [] });

// Loads the same webfonts the old server-rendered page did (catalyst_tracker/templates/catalyst_tracker/index.html's
// <head> block), only while this page is mounted, and sets --nav-h the way the page's own inline script did —
// querying this SPA's sticky app header instead of the old `.nav-header` element.
function useTrackerPageChrome() {
  useEffect(() => {
    document.body.classList.add('rct-body');
    const FONT_LINK_ID = 'catalyst-tracker-fonts';
    if (!document.getElementById(FONT_LINK_ID)) {
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect'; preconnect1.href = 'https://fonts.googleapis.com';
      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect'; preconnect2.href = 'https://fonts.gstatic.com'; preconnect2.crossOrigin = 'anonymous';
      const stylesheet = document.createElement('link');
      stylesheet.id = FONT_LINK_ID;
      stylesheet.rel = 'stylesheet';
      stylesheet.href = 'https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,600;6..72,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap';
      document.head.append(preconnect1, preconnect2, stylesheet);
    }
    const setNavH = () => {
      const h = document.querySelector('header');
      const pos = h ? getComputedStyle(h).position : '';
      document.body.style.setProperty('--nav-h', `${h && (pos === 'sticky' || pos === 'fixed') ? (h as HTMLElement).offsetHeight : 0}px`);
    };
    setNavH();
    window.addEventListener('resize', setNavH);
    const t = setTimeout(setNavH, 300);
    return () => {
      window.removeEventListener('resize', setNavH);
      clearTimeout(t);
      document.body.classList.remove('rct-body');
    };
  }, []);
}

const CatalystTrackerPage: React.FC = () => {
  useTrackerPageChrome();
  const { permissions } = useTrackerPermissions();

  const [catalysts, setCatalysts] = useState<TrackerCatalyst[]>([]);
  const [conferences, setConferences] = useState<TrackerConference[]>([]);
  const [changelog, setChangelog] = useState<TrackerChangelogEntry[]>([]);
  const [syncMeta, setSyncMeta] = useState<TrackerSyncMeta | null>(null);
  const [registry, setRegistry] = useState<TrackerRegistryDoc>({});
  const [suggestions, setSuggestions] = useState<TrackerSuggestion[]>([]);
  const [watch, setWatch] = useState<TrackerWatchDoc>(emptyWatch());
  const [panelHidden, setPanelHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filters, setFilters] = useState<TrackerFilterState>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<TrackerSort>('date');
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [filtersHidden, setFiltersHidden] = useState(() => {
    try { return localStorage.getItem('rcc-filters-hidden') === '1'; } catch { return false; }
  });
  const [perspOnly, setPerspOnly] = useState(() => {
    try { return localStorage.getItem('rcc-persp-only') === '1'; } catch { return false; }
  });

  const [suggestTarget, setSuggestTarget] = useState<TrackerCatalyst | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [perspEditorOpen, setPerspEditorOpen] = useState(false);

  const watchSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadCore = useCallback(async () => {
    try {
      const [cats, confs, log] = await Promise.all([
        catalystTrackerApi.listCatalysts(),
        catalystTrackerApi.listConferences(),
        catalystTrackerApi.listChangelog(),
      ]);
      setCatalysts(cats);
      setConferences(confs);
      setChangelog(log);
      setLoadError(null);
    } catch (e) {
      setLoadError('Could not load Catalyst Tracker data. Try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    catalystTrackerApi.getSyncMeta().then(setSyncMeta).catch(() => {});
    catalystTrackerApi.getRegistry().then(setRegistry).catch(() => {});
  }, []);

  useEffect(() => {
    loadCore();
    const interval = setInterval(loadCore, REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadCore]);

  useEffect(() => {
    if (!permissions?.userId) return;
    catalystTrackerApi.getWatch(permissions.userId)
      .then((doc) => { if (doc) setWatch({ ...emptyWatch(), ...doc.data }); })
      .catch(() => {});
  }, [permissions?.userId]);

  useEffect(() => {
    if (!permissions?.editor) return;
    catalystTrackerApi.listSuggestions().then(setSuggestions).catch(() => {});
  }, [permissions?.editor, queueOpen]);

  const watchedIds = useMemo(() => new Set(watch.catalysts || []), [watch.catalysts]);
  const isWatched = useCallback((id: string) => watchedIds.has(id), [watchedIds]);

  const saveWatch = useCallback((next: TrackerWatchDoc) => {
    setWatch(next);
    if (!permissions?.userId) return;
    if (watchSaveTimer.current) clearTimeout(watchSaveTimer.current);
    watchSaveTimer.current = setTimeout(() => {
      catalystTrackerApi.saveWatch(permissions.userId, { ...next, updatedAt: new Date().toISOString() }).catch(() => {});
    }, 300);
  }, [permissions?.userId]);

  const toggleFavorite = useCallback((id: string) => {
    setWatch((prev) => {
      const list = prev.catalysts || [];
      const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      const updated = { ...prev, catalysts: next };
      saveWatch(updated);
      return updated;
    });
  }, [saveWatch]);

  const removeFavorite = useCallback((id: string) => toggleFavorite(id), [toggleFavorite]);

  const setRead = useCallback((id: string, on: boolean) => {
    setWatch((prev) => {
      const read = { ...(prev.read || {}) };
      if (on) read[id] = new Date().toISOString(); else delete read[id];
      const updated = { ...prev, read };
      saveWatch(updated);
      return updated;
    });
  }, [saveWatch]);

  const markAllRead = useCallback(() => {
    setWatch((prev) => {
      const read = { ...(prev.read || {}) };
      const now = new Date().toISOString();
      (prev.catalysts || []).forEach((id) => { read[id] = now; });
      const updated = { ...prev, read };
      saveWatch(updated);
      return updated;
    });
  }, [saveWatch]);

  const activeConference = useMemo(() => conferences.find((c) => c.id === filters.meeting), [conferences, filters.meeting]);

  const filtered = useMemo(() => {
    const base = visibleCatalysts(catalysts, filters, activeConference, isWatched);
    // baseFilter(r) — tracker.js:245: "Relevant only" keeps rows perspOf() has something to say about
    // (covers both isMine()-owned rows and competitively-relevant ones), matching Ryan's filter exactly.
    if (watch.perspective && perspOnly) {
      return base.filter((r) => !!perspOf(catalysts, r, watch.perspective!));
    }
    return base;
  }, [catalysts, filters, activeConference, isWatched, watch.perspective, perspOnly]);
  const sorted = useMemo(() => sortCatalysts(filtered, sort, filters.when), [filtered, sort, filters.when]);

  // Timeline banner (tracker.js:1254's `S.company || S.hist`): a company or tumor-type scope plots its whole
  // history as a timeline, so it ignores the when/within (upcoming/past, next N days) toggles `filtered` applies.
  const bannerRows = useMemo(
    () => catalysts.filter((r) => baseFilter(r, filters, activeConference, isWatched)),
    [catalysts, filters, activeConference, isWatched],
  );
  const bannerTitle = [filters.company, filters.histology].filter(Boolean).join(' · ');
  const clearBanner = () => handleFilterChange({ company: '', histology: '' });

  const handleFilterChange = (next: Partial<TrackerFilterState>) => setFilters((f) => ({ ...f, ...next }));
  const handleReset = () => setFilters(DEFAULT_FILTERS);

  // Saved views (tracker.js's #fView): named filter-bar snapshots kept on the user's own watch doc.
  const [savedViewSel, setSavedViewSel] = useState('');
  const savedViews = watch.views || [];
  const handleSelectSavedView = (value: string) => {
    if (value === '__save') {
      const name = window.prompt('Name this view (up to 40 characters):', '')?.trim().slice(0, 40);
      if (!name) return;
      const views = [...savedViews];
      const i = views.findIndex((v) => v.name.toLowerCase() === name.toLowerCase());
      if (i >= 0) views[i] = { name, s: snapshotView(filters, sort) }; else views.push({ name, s: snapshotView(filters, sort) });
      saveWatch({ ...watch, views });
      setSavedViewSel(`v${i >= 0 ? i : views.length - 1}`);
      return;
    }
    if (value === '__del') {
      if (!savedViewSel) { window.alert('Pick a saved view first, then delete it.'); return; }
      const i = +savedViewSel.slice(1);
      const name = savedViews[i]?.name;
      if (!name || !window.confirm(`Delete the saved view "${name}"?`)) return;
      saveWatch({ ...watch, views: savedViews.filter((_, idx) => idx !== i) });
      setSavedViewSel('');
      return;
    }
    setSavedViewSel(value);
    if (!value) return;
    const view = savedViews[+value.slice(1)];
    if (!view) return;
    const { filters: f, sort: s } = applyView(view.s);
    setFilters((prev) => ({ ...prev, ...f }));
    if (s) setSort(s);
  };

  const activeFilterCount = useMemo(() => {
    const keys: (keyof TrackerFilterState)[] = ['company', 'histology', 'area', 'tier', 'type', 'q', 'meeting', 'within'];
    let n = keys.filter((k) => filters[k]).length;
    if (filters.delayedOnly) n += 1;
    if (filters.hideDelayed) n += 1;
    if (filters.highOnly) n += 1;
    if (filters.signal) n += 1;
    if (filters.watchOnly) n += 1;
    if (sort !== 'date') n += 1;
    return n;
  }, [filters, sort]);

  const toggleFiltersHidden = useCallback(() => {
    setFiltersHidden((v) => {
      const next = !v;
      try { localStorage.setItem('rcc-filters-hidden', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const applyPerspective = useCallback((company: string, only: boolean) => {
    setPerspOnly(only);
    try { localStorage.setItem('rcc-persp-only', only ? '1' : '0'); } catch { /* ignore */ }
    setWatch((prev) => { const next = { ...prev, perspective: company }; saveWatch(next); return next; });
  }, [saveWatch]);

  const turnOffPerspective = useCallback(() => applyPerspective('', false), [applyPerspective]);

  // deepUsed()/DEEP_MAX (tracker.js:1507-1511): a rolling-hour log of worker-search requests, reused
  // across the company lookup, general scan and per-catalyst "request a check" flows.
  const recordSearch = useCallback(() => {
    setWatch((prev) => {
      const now = new Date().toISOString();
      const log = [...(prev.searchLog || []), now].filter((t) => Date.now() - Date.parse(t) < 3600e3);
      const next = { ...prev, searchLog: log };
      saveWatch(next);
      return next;
    });
  }, [saveWatch]);

  const handleStat = (stat: 'all' | 'next' | 'flagged' | 'recent') => {
    if (stat === 'all') setFilters({ ...DEFAULT_FILTERS, when: 'all' });
    if (stat === 'next') setFilters({ ...DEFAULT_FILTERS, when: 'upcoming', within: '30' });
    if (stat === 'flagged') setFilters({ ...DEFAULT_FILTERS, when: 'all', delayedOnly: true });
    if (stat === 'recent') setFilters({ ...DEFAULT_FILTERS, when: 'past' });
    setView('list');
  };

  const openCorrection = (row: TrackerCatalyst) => { setSuggestTarget(row); setSuggestOpen(true); };
  const openNewSuggestion = () => { setSuggestTarget(null); setSuggestOpen(true); };
  const handleCalendarSelect = (row: TrackerCatalyst) => { setView('list'); handleFilterChange({ q: row.asset || row.company || '' }); };

  const openCatalystById = (id: string) => {
    const row = catalysts.find((r) => r.id === id);
    if (row) { setFilters({ ...DEFAULT_FILTERS, q: row.asset || row.company || '' }); setView('list'); }
    setLogOpen(false);
  };

  const pendingCount = suggestions.filter((s) => !s.status_review || s.status_review === 'pending').length;

  return (
    <div>
      <Header />
      <div className="rct">
        <div className="wrap">
          <header className="mast">
            <div className="brand">
              <span className="eyebrow">Ruby Oncology · Tracked catalysts</span>
              <h1>Catalyst Calendar</h1>
              <span className="asof">
                {syncMeta?.lastRun
                  ? `Auto-checked weekdays · last run ${fmtLong(syncMeta.lastRun.slice(0, 10))}${syncMeta.lastRunChanges != null ? ` · ${syncMeta.lastRunChanges} change${syncMeta.lastRunChanges === 1 ? '' : 's'}` : ''}`
                  : 'Auto-checked weekdays against primary sources'}
              </span>
            </div>
            <CatalystStatsBar rows={catalysts} onStat={handleStat} />
            <div className="mast-actions">
              <button
                type="button"
                className={`ib perspb${watch.perspective ? ' on' : ''}`}
                title={watch.perspective ? `Viewing as ${watch.perspective} · click to change` : 'Perspective: view catalysts as a company'}
                aria-label="Perspective"
                onClick={() => setPerspEditorOpen(true)}
              >
                <TrackerIcon name="eye" />
                {watch.perspective && <span className="pn">{watch.perspective.length > 18 ? `${watch.perspective.slice(0, 17)}…` : watch.perspective}</span>}
              </button>
              <button
                type="button"
                className="ib"
                aria-pressed={!filtersHidden}
                title={filtersHidden ? `Show filters${activeFilterCount ? ` (${activeFilterCount} active)` : ''}` : 'Hide filters'}
                aria-label={filtersHidden ? 'Show filters' : 'Hide filters'}
                style={filtersHidden ? undefined : { color: 'var(--ink)' }}
                onClick={toggleFiltersHidden}
              >
                <TrackerIcon name="sliders" />
                {filtersHidden && activeFilterCount > 0 && <span className="badge">{activeFilterCount}</span>}
              </button>
              {watchedIds.size > 0 && (
                <button type="button" className={`ib bellb${panelHidden ? '' : ' on'}`} title={panelHidden ? 'Show favorites panel' : 'Hide favorites panel'} aria-label="Favorites panel" onClick={() => setPanelHidden((v) => !v)}>
                  <TrackerIcon name="star" />
                </button>
              )}
              <button type="button" className="ib" title="Update log" aria-label="Update log" onClick={() => setLogOpen(true)}>
                <TrackerIcon name="history" />
              </button>
              {permissions?.editor && (
                <button type="button" className="ib" title="Suggestions awaiting verification" aria-label="Suggestions awaiting verification" onClick={() => setQueueOpen(true)}>
                  <TrackerIcon name="inbox" />
                  {pendingCount > 0 && <span className="badge">{pendingCount}</span>}
                </button>
              )}
              {permissions?.editor && (
                <button type="button" className="ib primary tip-r" id="addBtn" title="Suggest a catalyst for verification" aria-label="Suggest a catalyst" onClick={openNewSuggestion}>
                  <TrackerIcon name="plus" />
                </button>
              )}
            </div>
          </header>

          {loadError && <div className="auto warn"><span>{loadError}</span></div>}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><span className="spin" /></div>
          ) : (
            <>
              {!filtersHidden && (
                <CatalystFilterBar
                  rows={catalysts}
                  conferences={conferences}
                  filters={filters}
                  onChange={handleFilterChange}
                  onReset={handleReset}
                  sort={sort}
                  onSort={setSort}
                  view={view}
                  onView={setView}
                  savedViews={savedViews}
                  savedViewValue={savedViewSel}
                  onSelectSavedView={handleSelectSavedView}
                />
              )}
              <WatchPanel
                rows={catalysts}
                watch={watch}
                onRemove={removeFavorite}
                onOpen={handleCalendarSelect}
                onMarkRead={(id) => setRead(id, true)}
                onMarkAllRead={markAllRead}
                hidden={panelHidden}
                onHide={() => setPanelHidden(true)}
                watchOnly={filters.watchOnly}
                onToggleWatchOnly={() => handleFilterChange({ watchOnly: !filters.watchOnly })}
                onOpenAlerts={() => setAlertsOpen(true)}
              />
              <PerspectivePanel
                rows={catalysts}
                company={watch.perspective || ''}
                only={perspOnly}
                editing={perspEditorOpen}
                onEditingChange={setPerspEditorOpen}
                onApply={applyPerspective}
                onOff={turnOffPerspective}
                permissions={permissions}
                searchLog={watch.searchLog}
                onRecordSearch={recordSearch}
              />
              {(filters.company || filters.histology) && (
                <TimelineBanner
                  rows={bannerRows}
                  registry={registry}
                  conferences={conferences}
                  title={bannerTitle}
                  companyActive={!!filters.company}
                  histologyActive={!!filters.histology}
                  onClear={clearBanner}
                  onOpenCatalyst={openCatalystById}
                />
              )}
              <span className="count">{sorted.length} of {catalysts.length} catalysts</span>
              {view === 'list' ? (
                <CatalystListView
                  rows={sorted}
                  allRows={catalysts}
                  perspectiveCompany={watch.perspective}
                  watchedIds={watchedIds}
                  onToggleFavorite={toggleFavorite}
                  permissions={permissions}
                  onSuggestCorrection={openCorrection}
                  readMap={watch.read}
                  onToggleRead={setRead}
                  onOpenCatalyst={openCatalystById}
                />
              ) : (
                <CatalystCalendarView rows={sorted} watchedIds={watchedIds} onSelect={handleCalendarSelect} />
              )}
            </>
          )}
        </div>
      </div>

      {suggestOpen && (
        <SuggestCatalystModal
          target={suggestTarget}
          rows={catalysts}
          onClose={() => setSuggestOpen(false)}
          onSubmitted={() => { loadCore(); if (permissions?.editor) catalystTrackerApi.listSuggestions().then(setSuggestions).catch(() => {}); }}
        />
      )}

      {queueOpen && (
        <SuggestionsQueueModal
          suggestions={suggestions}
          canReview={!!permissions?.owner}
          onClose={() => setQueueOpen(false)}
          onReviewed={() => { catalystTrackerApi.listSuggestions().then(setSuggestions).catch(() => {}); loadCore(); }}
        />
      )}

      {logOpen && (
        <TrackerModal title="Update log" onClose={() => setLogOpen(false)}>
          <ChangelogView entries={changelog} sync={syncMeta} onOpenCatalyst={openCatalystById} />
        </TrackerModal>
      )}

      {alertsOpen && (
        <EmailAlertsModal
          watch={watch}
          defaultEmail={permissions?.email || ''}
          onClose={() => setAlertsOpen(false)}
          onSave={({ email, emailAlerts }) => saveWatch({ ...watch, email, emailAlerts })}
        />
      )}
    </div>
  );
};

export default CatalystTrackerPage;
